import { Link } from 'react-router-dom';
import { useTranslation } from '@mypoz/i18n';
import { Card, Icon, cn } from '@mypoz/ui';

import { actionLinkClass } from '@/lib/styles.js';
import { useFormat } from '@/lib/format.js';

/**
 * What the tile knows about its number.
 *
 * A discriminated union rather than `count?: number` alongside an `isLoading` flag, so a caller
 * cannot describe a state that does not exist — a stale count while loading, or an error that
 * still carries a number. MYP-27 replaces the Dashboard's fixtures with real request state; this
 * is the shape that state has to collapse into.
 */
export type StatTileValue =
  | { readonly status: 'loading' }
  | { readonly status: 'error' }
  | { readonly status: 'ready'; readonly count: number };

export interface StatTileProps {
  /** Eyebrow above the number, e.g. "Ready to order". */
  label: string;
  value: StatTileValue;
  /** Subtitle when the count is one or more. */
  hint: string;
  /**
   * Subtitle when the count is exactly zero. Separate from `hint` because zero is the resting
   * state of at least one of these tiles, not an edge case — "nothing waiting" reads very
   * differently from "0 packages in warehouse".
   */
  emptyHint: string;
  action: { readonly label: string; readonly to: string };
  onRetry?: () => void;
}

/**
 * A Dashboard counter: eyebrow, number, one line of context, one call to action.
 *
 * Domain strings arrive already translated, because the tile has no idea what it is counting.
 * The three generic state strings (loading, error, retry) are resolved here from the shared
 * catalog keys, so every instance describes those states identically.
 *
 * Deliberately not `@mypoz/ui`'s `StatCard`, which is a horizontal icon-and-value row with no
 * call to action and no loading or error state. Kept feature-local rather than promoted to the
 * shared package until a second feature actually wants it.
 */
export function StatTile({ label, value, hint, emptyHint, action, onRetry }: StatTileProps) {
  const { t } = useTranslation();
  const fmt = useFormat();

  const isEmpty = value.status === 'ready' && value.count === 0;

  return (
    <Card aria-busy={value.status === 'loading'} className="flex h-full flex-col p-5 sm:p-6">
      <p className="text-xs font-semibold tracking-[0.08em] text-muted uppercase">{label}</p>

      {/*
        The number. `tabular-nums` matters more than it looks: without it the glyphs are
        proportional, so the tile visibly twitches when a count goes 8 → 10 on refresh.
      */}
      {value.status === 'loading' ? (
        <span className="mt-3 block h-9 w-20 animate-pulse rounded-md bg-line">
          <span className="sr-only">{t('state.loading')}</span>
        </span>
      ) : (
        <p
          className={cn(
            'mt-3 text-4xl leading-none font-bold tracking-tight tabular-nums',
            value.status === 'error' || isEmpty ? 'text-muted' : 'text-ink',
          )}
        >
          {value.status === 'error' ? '—' : fmt.number(value.count)}
        </p>
      )}

      {/*
        Two lines are reserved whether or not the subtitle needs them. At tablet widths one tile's
        hint wraps and another's does not, and without the reserve the three call-to-action
        baselines end up at different heights.
      */}
      <div className="mt-2 min-h-10">
        {value.status === 'loading' ? (
          <span className="mt-1 block h-4 w-32 animate-pulse rounded bg-line" />
        ) : (
          <p className="text-sm text-muted">
            {value.status === 'error' ? t('state.error') : isEmpty ? emptyHint : hint}
          </p>
        )}
      </div>

      {/*
        At zero the call to action is removed rather than disabled: "Create order" with nothing to
        order sends the reader to an empty screen. `mt-auto` pins whatever is left to the bottom,
        so the remaining tiles' links stay aligned across the grid row.
      */}
      <div className="mt-auto pt-4">
        {value.status === 'error' && onRetry ? (
          <button type="button" onClick={onRetry} className={actionLinkClass()}>
            {t('actions.retry')}
            <Icon name="refresh" size={17} />
          </button>
        ) : value.status === 'ready' && !isEmpty ? (
          <Link to={action.to} className={actionLinkClass()}>
            {action.label}
            <Icon name="arrow_forward" size={17} />
          </Link>
        ) : null}
      </div>
    </Card>
  );
}
